/**
 * Local store — Dexie (IndexedDB) is the source of truth on the device.
 * The UI reads and writes here; the sync engine reconciles with Supabase.
 *
 * Works on web and inside the Capacitor WebView (WKWebView persists
 * IndexedDB in the app container). Call `requestPersistentStorage()` on
 * launch. Do not add the SQLite plugin unless a device test shows eviction.
 */
import Dexie, { type Table } from 'dexie';
import type {
  AttendanceRow,
  EvalPlayerRow,
  EvalScoreRow,
  LibraryItemRow,
  LineupRow,
  OutboxEntry,
  PlanFolderRow,
  PlanRow,
  PlayerRow,
  SyncStateRow,
  SyncTable,
} from '../types';

export interface KvRow {
  key: string;
  value: unknown;
}

export class CoachDb extends Dexie {
  plans!: Table<PlanRow, string>;
  plan_folders!: Table<PlanFolderRow, string>;
  library_items!: Table<LibraryItemRow, string>;
  players!: Table<PlayerRow, string>;
  attendance!: Table<AttendanceRow, string>;
  eval_players!: Table<EvalPlayerRow, string>;
  eval_scores!: Table<EvalScoreRow, string>;
  lineups!: Table<LineupRow, string>;
  outbox!: Table<OutboxEntry, string>;
  sync_state!: Table<SyncStateRow, SyncTable>;
  /** Small per-device state (current team, auto-start marks, scratch pad). Never synced. */
  kv!: Table<KvRow, string>;

  constructor(name = 'coach-planner') {
    super(name);
    this.version(1).stores({
      plans: 'id, team_id, folder_id, updated_at, deleted_at, [team_id+deleted_at]',
      plan_folders: 'id, team_id, updated_at, [team_id+deleted_at]',
      library_items: 'id, team_id, kind, updated_at, [team_id+kind], [team_id+kind+deleted_at]',
      players: 'id, team_id, number, updated_at, [team_id+deleted_at]',
      attendance: 'id, team_id, player_id, practice_date, updated_at, [team_id+practice_date], [player_id+practice_date]',
      eval_players: 'id, team_id, updated_at, [team_id+deleted_at]',
      eval_scores: 'id, team_id, eval_player_id, updated_at, [eval_player_id+day+coach_id]',
      lineups: 'id, team_id, updated_at',
      outbox: 'id, table, row_id, queued_at, rejected, [table+row_id]',
      sync_state: 'table',
      kv: 'key',
    });
  }

  table_(name: SyncTable): Table<{ id: string; team_id: string; updated_at: string; deleted_at: string | null }, string> {
    return this.table(name) as never;
  }
}

export const SYNC_TABLES: SyncTable[] = [
  'plan_folders',
  'plans',
  'library_items',
  'players',
  'attendance',
  'eval_players',
  'eval_scores',
  'lineups',
];

let _db: CoachDb | null = null;
export function getDb(): CoachDb {
  if (!_db) _db = new CoachDb();
  return _db;
}
/** Tests: swap in a fresh database. */
export function setDb(db: CoachDb | null): void {
  _db = db;
}

export async function requestPersistentStorage(): Promise<boolean> {
  try {
    const nav = globalThis.navigator as Navigator | undefined;
    if (nav?.storage?.persist) return await nav.storage.persist();
  } catch {
    /* ignore */
  }
  return false;
}

export async function kvGet<T>(key: string): Promise<T | undefined> {
  const r = await getDb().kv.get(key);
  return r?.value as T | undefined;
}
export async function kvSet(key: string, value: unknown): Promise<void> {
  await getDb().kv.put({ key, value });
}
